import { printVoucher, printVoucherTemplateData } from '@/utilities/paymentHelper'

import type { VoucherResponse } from '../types/pos/deposit'
import {
    deviceConfigurationService,
    DeviceJob} from './deviceConfigurationService'

const PRINT_VOUCHER_TEMPLATE_JOB_TYPE = 'printVoucherTemplate'
const TEST_PRINTER_JOB_TYPE = 'testPrinter'
const TEST_PRINTER_TEMPLATE_NAME = 'terminal_test_print'

type PrintVoucherTemplateJobInput = {
    voucherDetails?: Record<string, unknown>
    voucherResponse?: VoucherResponse
    voucherTypeId?: string
    templateName?: string
    deviceName?: string
    vendorName?: string
    vendorUserId?: string
}

class DeviceJobService {
    private readonly inProgressJobIds = new Set<number>()

    async executeJobs(jobs?: DeviceJob[]): Promise<void> {
        if (!jobs?.length) {
            return
        }

        for (const job of jobs) {
            await this.executeJob(job)
        }
    }

    private async executeJob(job: DeviceJob): Promise<void> {
        if (this.inProgressJobIds.has(job.deviceJobId)) {
            return
        }

        this.inProgressJobIds.add(job.deviceJobId)

        try {
            switch (job.jobType) {
                case PRINT_VOUCHER_TEMPLATE_JOB_TYPE:
                    await this.executePrintVoucherTemplateJob(job)
                    break
                case TEST_PRINTER_JOB_TYPE:
                    await this.executeTestPrinterJob()
                    break
                default:
                    throw new Error(`Unsupported device job type: ${job.jobType}`)
            }

            await deviceConfigurationService.reportJobSuccess(job.deviceJobId)
        } catch (error) {
            const message =
                error instanceof Error ? error.message : 'Unknown job error'
            await deviceConfigurationService.reportJobFailure(
                job.deviceJobId,
                message
            )
        } finally {
            this.inProgressJobIds.delete(job.deviceJobId)
        }
    }

    private async executePrintVoucherTemplateJob(job: DeviceJob): Promise<void> {
        const input = job.input as PrintVoucherTemplateJobInput
        const templateName = input.templateName ?? 'terminal_voucher'
        const voucherTypeId = input.voucherTypeId ?? '20'

        const result = input.voucherDetails
            ? await printVoucherTemplateData(
                  input.voucherDetails,
                  () => {},
                  undefined,
                  templateName
              )
            : await printVoucher(
                  this.resolveVoucherResponse(input),
                  voucherTypeId,
                  () => {},
                  undefined,
                  templateName,
                  {
                      deviceName: input.deviceName,
                      vendorName: input.vendorName,
                      vendorUserId: input.vendorUserId
                  }
              )

        if (!result.success) {
            throw new Error(result.message)
        }
    }

    private async executeTestPrinterJob(): Promise<void> {
        const result = await printVoucherTemplateData(
            {},
            () => {},
            undefined,
            TEST_PRINTER_TEMPLATE_NAME
        )

        if (!result.success) {
            throw new Error(result.message)
        }
    }

    private resolveVoucherResponse(
        input: PrintVoucherTemplateJobInput
    ): VoucherResponse {
        const voucherResponse =
            input.voucherResponse ??
            ('moneyTransfer' in input
                ? (input as unknown as VoucherResponse)
                : undefined)

        if (!voucherResponse?.moneyTransfer) {
            throw new Error(
                'printVoucherTemplate job requires voucherDetails, voucherResponse, or moneyTransfer input'
            )
        }

        return voucherResponse
    }
}

export const deviceJobService = new DeviceJobService()
export { PRINT_VOUCHER_TEMPLATE_JOB_TYPE, TEST_PRINTER_JOB_TYPE }
